<script setup lang="ts">
import type { DateValue } from "@internationalized/date"
import { parseDate } from "@internationalized/date"
import { Calendar as CalendarIcon } from "lucide-vue-next"
import { computed, ref, shallowRef, watch } from "vue"

import { Calendar } from "@/components/ui/calendar"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

defineOptions({
  inheritAttrs: false,
})

const modelValue = defineModel<Date | string | null | undefined>()

const props = defineProps<{
  placeholder?: string
  disabled?: boolean
  minDate?: string | Date // ISO string
  maxDate?: string | Date // ISO string
}>()

const openState = ref(false)
const isOpen = computed({
  get: () => openState.value,
  set: (val) => props.disabled || (openState.value = val),
})

const calendarValue = shallowRef<DateValue | undefined>(
  parseDateProp(modelValue.value || new Date()),
)
const calendarDefaultDate = computed(() => {
  return calendarValue.value || parseDateProp(modelValue.value || new Date())
})
const minDateValue = computed(() => {
  if (!props.minDate) return undefined
  return parseDate(dateToString(new Date(props.minDate)))
})
const maxDateValue = computed(() => {
  if (!props.maxDate) return undefined
  return parseDate(dateToString(new Date(props.maxDate)))
})

const displayValue = computed(() => {
  if (!modelValue.value) return ""
  return dateToString(new Date(modelValue.value), "es-ES")
})

function dateToString(date: Date, locale = "fr-CA") {
  return date.toLocaleDateString(locale)
}

function onSelect(val: DateValue | undefined) {
  if (!val) return
  if (modelValue.value instanceof Date) {
    modelValue.value = new Date(val.toString())
  } else {
    modelValue.value = dateToString(new Date(val.toString()))
  }
  calendarValue.value = val
  isOpen.value = false
}

function parseDateProp(date: string | Date | null | undefined) {
  if (!date) return undefined
  return parseDate(dateToString(new Date(date)))
}

watch(modelValue, (newVal) => (calendarValue.value = parseDateProp(newVal)))
</script>

<template>
  <Popover v-model:open="isOpen" :disabled="props.disabled">
    <PopoverTrigger as-child>
      <InputGroup :disabled="props.disabled">
        <InputGroupInput
          :model-value="displayValue"
          v-bind="$attrs"
          readonly
          :disabled="props.disabled"
          :placeholder="props.placeholder || 'Seleccionar fecha'"
        />
        <InputGroupAddon>
          <CalendarIcon @click.prevent />
        </InputGroupAddon>
      </InputGroup>
    </PopoverTrigger>
    <PopoverContent class="w-auto p-0" align="start">
      <Calendar
        v-model="calendarValue"
        :default-placeholder="calendarDefaultDate"
        :min-value="minDateValue"
        :max-value="maxDateValue"
        layout="month-and-year"
        locale="es-ES"
        initial-focus
        @update:model-value="onSelect"
      />
    </PopoverContent>
  </Popover>
</template>
